import { useCallback, useEffect, useMemo, useState } from "react";
import { useLoaderData, useRouteLoaderData } from "react-router-dom";
import CryptoJS from "crypto-js";
import LZString from "lz-string";
import { daysPerspectives } from "./constants";

const SECRET_KEY = "Maximum Effort";

export function useUserFormState(moduleName, extraDefaults) {
    const initialState = useMemo(
        () => ({
            station: [],
            market: [],
            daypart: [],
            daypartId: "placeholder",
            demo: [],
            dataType: 1,
            hourRange: [0, 24],
            hour: 8,
            minuteRange: [0, 60],
            days: daysPerspectives[0].days,
            perspective: JSON.stringify(daysPerspectives[0].days),
            ...extraDefaults,
        }),
        [extraDefaults]
    );

    const { userInfo, radios, competitorRadios, demographics } = useRouteLoaderData("root");
    const loaderData = useLoaderData();
    const { dayparts } = loaderData || {};
    const [formData, setFormData] = useState(initialState);

    // Convert Ids to full objects
    const loadFromIds = useCallback(
        async parsedData => {
            if (parsedData.station && typeof parsedData.station === "number") {
                const resolvedRadios = (await radios).data;
                const station =
                    resolvedRadios.find(x => x.radio_id === parsedData.station) ?? (await competitorRadios).data.find(x => x.radio_id === parsedData.station);
                parsedData.station = station ? [station] : [];
            }
            if (parsedData.daypart && typeof parsedData.daypart === "number") {
                const resolvedDayparts = (await dayparts).data;
                const daypart = resolvedDayparts.find(x => x.daypart_id === parsedData.daypart);
                parsedData.daypart = daypart ? [daypart] : [];
            }
            if (parsedData.demo && typeof parsedData.demo === "number") {
                const resolvedDemos = (await demographics).data;
                const demo = resolvedDemos.find(x => x.demo_id === parsedData.demo);
                parsedData.demo = demo ? [demo] : [];
            }
            return parsedData;
        },
        [radios, competitorRadios, dayparts, demographics]
    );

    // Decrypt and decompress saved data
    const parseFormData = useCallback(
        async key => {
            try {
                const savedData = localStorage.getItem(key);
                if (savedData) {
                    const decompressedData = LZString.decompressFromUTF16(savedData);
                    const bytes = CryptoJS.AES.decrypt(decompressedData, SECRET_KEY);
                    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
                    const parsedRawData = JSON.parse(decryptedData);
                    const parsedFullData = await loadFromIds(parsedRawData);
                    return parsedFullData;
                }
            } catch (error) {
                console.error("Error parsing localStorage data:", error);
                return initialState;
            }
        },
        [initialState, loadFromIds]
    );

    // Load default (main) selection on page load
    useEffect(() => {
        async function parseDefaultFormData() {
            const user = (await userInfo).data;
            const userId = user?.user_id;
            const key = `${userId}_${moduleName}_main_formData`;
            const parsedData = await parseFormData(key);
            setFormData(prevData => ({
                ...prevData,
                ...parsedData,
            }));
        }
        parseDefaultFormData();
    }, [moduleName, parseFormData, userInfo]);

    // Remove properties we do not want to save/load
    function prepareFormForSaving(formDataCopy) {
        if (formDataCopy.station.length) formDataCopy.station = formDataCopy.station[0].radio_id;
        if (formDataCopy.daypart.length) formDataCopy.daypart = formDataCopy.daypart[0].daypart_id;
        if (formDataCopy.demo.length) formDataCopy.demo = formDataCopy.demo[0].demo_id;
        delete formDataCopy.date;
        delete formDataCopy.dateRange;
        delete formDataCopy.perspective;
        return formDataCopy;
    }

    // Compress, encrypt and save data
    const saveFormData = async (extraKey = "main") => {
        const userId = (await userInfo).data.user_id;
        const key = `${userId}_${moduleName}_${extraKey}_formData`;
        const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(prepareFormForSaving({ ...formData })), SECRET_KEY).toString();
        const compressedData = LZString.compressToUTF16(encryptedData);
        localStorage.setItem(key, compressedData);
    };

    // Load specific selection
    const loadFormData = useCallback(
        async (extraKey = "main") => {
            const userId = (await userInfo).data.user_id;
            const key = `${userId}_${moduleName}_${extraKey}_formData`;
            const parsedData = await parseFormData(key);
            return parsedData;
        },
        [moduleName, parseFormData, userInfo]
    );

    return [formData, setFormData, saveFormData, loadFormData];
}
